import {
  addOrganisationToMember,
  createInvitation,
  deleteMember,
  deleteOrganisationFromMember,
  resendInvite,
  updateMember
} from '../../predict/src/core/api/members';
import { clearWorkspaceMembersQueryCache } from '../queries/members';
import { createMutation } from '../utils/mutations';
import {
  WorkspaceMemberOrganisationPatch,
  WorkspaceMemberPatch,
  WorkspaceMemberRolePatch
} from '../../predict/src/core/types';

export interface AddOrganisationsToMemberInput {
  memberId: string;
  organisations: WorkspaceMemberOrganisationPatch[];
}

export interface DeleteOrganisationFromMemberInput {
  memberId: string;
  organisationId: string;
}

export interface UpdateMemberRoleInput {
  memberId: string;
  role: WorkspaceMemberRolePatch['role'];
}

type AddOrganisationsToMemberResult = Awaited<ReturnType<typeof addOrganisationToMember>>;
type CreateInvitationResult = Awaited<ReturnType<typeof createInvitation>>;
type DeleteMembersResult = Awaited<ReturnType<typeof deleteMember>>[];
type DeleteOrganisationFromMemberResult = Awaited<ReturnType<typeof deleteOrganisationFromMember>>;
type ResendInviteResult = Awaited<ReturnType<typeof resendInvite>>;
type UpdateMemberRoleResult = Awaited<ReturnType<typeof updateMember>>;

const invalidateWorkspaceMembers = (workspaceId: string): void => {
  clearWorkspaceMembersQueryCache(workspaceId);
};

export const addOrganisationsToMemberMutation = (workspaceId: string) =>
  createMutation<AddOrganisationsToMemberInput, AddOrganisationsToMemberResult>({
    mutationFn: ({ memberId, organisations }) =>
      addOrganisationToMember({ organisations }, workspaceId, memberId),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });

export const createInvitationMutation = (workspaceId: string) =>
  createMutation<WorkspaceMemberPatch[], CreateInvitationResult>({
    mutationFn: invitations => createInvitation(workspaceId, invitations),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });

export const deleteMembersMutation = (workspaceId: string) =>
  createMutation<string[], DeleteMembersResult>({
    mutationFn: memberIds => Promise.all(memberIds.map(memberId => deleteMember(workspaceId, memberId))),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });

export const deleteOrganisationFromMemberMutation = (workspaceId: string) =>
  createMutation<DeleteOrganisationFromMemberInput, DeleteOrganisationFromMemberResult>({
    mutationFn: ({ memberId, organisationId }) =>
      deleteOrganisationFromMember(workspaceId, memberId, organisationId),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });

export const resendInviteMutation = (workspaceId: string) =>
  createMutation<string, ResendInviteResult>({
    mutationFn: memberId => resendInvite(workspaceId, memberId),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });

export const updateMemberRoleMutation = (workspaceId: string) =>
  createMutation<UpdateMemberRoleInput, UpdateMemberRoleResult>({
    mutationFn: ({ memberId, role }) => updateMember({ role }, workspaceId, memberId),
    onSuccess: () => invalidateWorkspaceMembers(workspaceId)
  });
